import type { FC } from "react";
import { useCallback, useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useCompetition } from "shared/hooks";
import type { MatchesFilterModel } from "shared/types";
import { MatchStatusEnum } from "shared/types";

import type { BaseBlockFilterModel } from "shared/types/FilterModel";
import { MatchesFilters as MatchesFiltersKeys } from "shared/types/GroupScheduleTypes";
import { MatchScheduleStatusEnum } from "shared/types/MatchStatusEnum";

import FilterPopover from "../FilterPopover";
import type { FilterBlockModel } from "../FilterPopover/FilterBlock";
import { FilterBlockTypes } from "../FilterPopover/FilterBlock";

interface MatchesFiltersProps {
	competitionId?: string;
	onChangeFilters: (filters: MatchesFilterModel) => void;
	isTournamentMatches?: boolean;
	loading?: boolean;
}

const MatchesFilters: FC<MatchesFiltersProps> = ({ competitionId, onChangeFilters, isTournamentMatches, loading }) => {
	const { t } = useTranslation();

	const { getCompetitionDivisions, listCompetitionFacilityFields, getData: getCompetitionData } = useCompetition();
	const { divisions, facilityFields } = getCompetitionData();

	useEffect(() => {
		if (competitionId) {
			getCompetitionDivisions({
				competitionId
			});
			listCompetitionFacilityFields(competitionId);
		}
	}, [getCompetitionDivisions, listCompetitionFacilityFields, competitionId]);

	const filterBlocks = useMemo((): FilterBlockModel[] => {
		const options: FilterBlockModel[] = [
			{
				id: MatchesFiltersKeys.status,
				type: FilterBlockTypes.CHECKBOX,
				title: t("match:match_status"),
				options: [
					{
						label: t("invitation:created"),
						value: [MatchStatusEnum.NEEDS_TIME, MatchStatusEnum.NEEDS_LOCATION]
					},
					{
						label: t("match:scheduled"),
						value: [MatchStatusEnum.NEEDS_REFEREES]
					},
					{
						label: t("match:ready_to_play"),
						value: [MatchStatusEnum.READY_TO_BE_PLAYED]
					}
				]
			}
		];

		if (isTournamentMatches) {
			if (!!divisions?.length) {
				options.push({
					id: MatchesFiltersKeys.divisionIds,
					type: FilterBlockTypes.CHECKBOX,
					title: t("divisions"),
					options: divisions.map(division => ({
						label: division.name,
						value: division.id
					}))
				});
			}

			options.push({
				id: MatchesFiltersKeys.matchNumberSearch,
				type: FilterBlockTypes.INPUT,
				title: t("match:match_number"),
				inputText: "",
				inputPlaceholder: "1-10"
			});

			options.push({
				id: MatchesFiltersKeys.teamSearch,
				type: FilterBlockTypes.INPUT,
				title: `${t("team")}/${t("club")}`,
				inputText: ""
			});

			options.push({
				id: MatchesFiltersKeys.scheduleStatus,
				type: FilterBlockTypes.RADIO,
				title: t("schedule"),
				options: [
					{
						label: t("competition:without_schedule"),
						value: MatchScheduleStatusEnum.EMPTY
					},
					{
						label: t("competition:with_schedule"),
						value: MatchScheduleStatusEnum.EXISTS
					},
					{
						label: t("competition:conflicts_schedule"),
						value: MatchScheduleStatusEnum.CONFLICT
					}
				]
			});

			options.push({
				id: MatchesFiltersKeys.overdueTime,
				type: FilterBlockTypes.INPUT,
				title: t("overdue_time"),
				inputText: "",
				inputPlaceholder: "10",
				inputType: "number"
			});
		}

		if (facilityFields?.length) {
			options.push({
				id: MatchesFiltersKeys.fieldIds,
				type: FilterBlockTypes.CHECKBOX,
				title: t("field"),
				options: facilityFields.map(field => ({
					label: field.name ?? "",
					value: field.id
				}))
			});
		}

		return options;
	}, [divisions, facilityFields, isTournamentMatches, t]);

	const applyFilters = useCallback(
		(blocksValues?: BaseBlockFilterModel[]) => {
			if (blocksValues?.length) {
				const filterParams: Partial<MatchesFilterModel> = {};
				blocksValues.forEach(block => {
					switch (block.blockId) {
						case MatchesFiltersKeys.status:
							filterParams[MatchesFiltersKeys.status] = block.values?.length
								? (block.values as MatchStatusEnum[])
								: undefined;
							break;

						case MatchesFiltersKeys.divisionIds:
							filterParams[MatchesFiltersKeys.divisionIds] = block.values?.length
								? (block.values as string[])
								: undefined;
							break;

						case MatchesFiltersKeys.matchNumberSearch:
							filterParams.matchNumberSearch = block.inputText || "";
							break;

						case MatchesFiltersKeys.teamSearch:
							filterParams.teamSearch = block.inputText || "";
							break;

						case MatchesFiltersKeys.fieldIds:
							filterParams[MatchesFiltersKeys.fieldIds] = block.values?.length ? (block.values as string[]) : undefined;
							break;

						case MatchesFiltersKeys.scheduleStatus:
							filterParams[MatchesFiltersKeys.scheduleStatus] = block.radioValue
								? (block.radioValue as MatchScheduleStatusEnum)
								: undefined;
							break;

						case MatchesFiltersKeys.overdueTime:
							filterParams.overdueTime = block.inputText ? Number(block.inputText) || 0 : undefined;
							break;
					}
				});
				onChangeFilters(filterParams);
			}
			return;
		},
		[onChangeFilters]
	);

	const resetFilters = useCallback(() => {
		onChangeFilters({});
	}, [onChangeFilters]);

	return (
		<FilterPopover
			onApplyFilters={applyFilters}
			onClearFilters={resetFilters}
			filterBlocks={filterBlocks}
			loading={loading}
		/>
	);
};

export default MatchesFilters;
